import PropTypes from 'prop-types';
import { Box, CircularProgress, Skeleton, Typography } from '@mui/material';
import Drawer from 'features/IRWorkstation/components/Common/Drawer/Drawer';
import SupportingDataRow from './SupportingDataRow';
import { formatDate, formatCurrency } from 'utils/utils';

const mapInteraction = (record) => ({
  label: 'INTERACTION',
  primaryText: record.name || record.type || 'Interaction',
  secondaryText: [record.type, record.date ? formatDate(record.date, true) : null]
    .filter(Boolean)
    .join(' · '),
  salesforceUrl: record.salesforce_url,
});

const mapInvestor = (record) => ({
  label: 'INVESTOR COMMITMENT',
  primaryText: record.name || 'Investor',
  secondaryText: [
    record.commitment ? formatCurrency(record.commitment) : null,
    record.commitment_date ? formatDate(record.commitment_date, true) : null,
  ]
    .filter(Boolean)
    .join(' · '),
  salesforceUrl: record.salesforce_url,
});

const mapFundraising = (record) => ({
  label: 'FUNDRAISING OPPORTUNITY',
  primaryText: record.name || 'Fundraising',
  secondaryText: [
    record.commitment ? formatCurrency(record.commitment) : null,
    record.stage,
  ]
    .filter(Boolean)
    .join(' · '),
  salesforceUrl: record.salesforce_url,
});

const getSupportingRecords = (justification) => {
  const data = justification?.supporting_data;
  if (!data) return [];

  switch (justification.type) {
    case 'engagement_cadence':
      return (data.interaction_references || []).map(mapInteraction);
    case 'historical_pattern':
      return (data.investors || []).map(mapInvestor);
    case 'process_momentum':
      return (data.fundraisings || []).map(mapFundraising);
    default:
      return [];
  }
};

const HealthScoreDrawer = ({ open, justification, date, regenerating, onClose }) => {
  const supportingRecords = getSupportingRecords(justification);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={justification?.label || 'Justification'}
      subtitle={date ? `As of ${formatDate(date, true)}` : ''}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* AI-Generated Summary */}
        {(justification?.summary || regenerating) && (
          <Box sx={{ padding: '20px 24px' }}>
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                textTransform: 'uppercase',
                color: 'text.tertiary',
                letterSpacing: '0.5px',
                mb: '8px',
              }}
            >
              AI-Generated Summary
            </Typography>
            {regenerating ? (
              <>
                <Skeleton data-testid='drawer-summary-skeleton' variant='text' width='95%' sx={{ fontSize: '13px' }} />
                <Skeleton variant='text' width='85%' sx={{ fontSize: '13px' }} />
                <Skeleton variant='text' width='60%' sx={{ fontSize: '13px' }} />
              </>
            ) : (
              justification?.summary && (
                <Typography
                  sx={{
                    fontSize: '13px',
                    color: 'text.primary',
                    lineHeight: 1.5,
                  }}
                >
                  {justification.summary}
                </Typography>
              )
            )}
            {regenerating && (
              <Box
                data-testid='drawer-summary-regenerating'
                sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: '8px' }}
              >
                <CircularProgress size={10} thickness={6} sx={{ color: 'text.secondary' }} />
                <Typography sx={{ fontSize: '11px', fontStyle: 'italic', color: 'text.secondary' }}>
                  Updating summary...
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Supporting Data */}
        {supportingRecords.length > 0 && (
          <Box sx={{ flex: 1, overflowY: 'auto', borderTop: '1px solid', borderColor: 'border.light' }}>
            <Box sx={{ padding: '16px 24px 8px' }}>
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: 'text.tertiary',
                  letterSpacing: '0.5px',
                }}
              >
                Supporting Data
              </Typography>
            </Box>
            <Box>
              {supportingRecords.map((record, index) => (
                <SupportingDataRow
                  key={index}
                  label={record.label}
                  primaryText={record.primaryText}
                  secondaryText={record.secondaryText}
                  salesforceUrl={record.salesforceUrl}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

HealthScoreDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  justification: PropTypes.object,
  date: PropTypes.string,
  regenerating: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

export default HealthScoreDrawer;
